import { MouseEvent, ReactElement } from 'react';
import cn from 'classnames';
import usePagination from '@mui/material/usePagination';
import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import { useTranslation } from 'next-i18next';

import styles from './PaginationNav.module.scss';

export interface PaginationNavProps {
  /** 1-indexed current page. */
  currentPage: number;
  /** Total number of pages available. */
  totalPages: number;
  /** Builds the href for a given 1-indexed page number. */
  createHref: (page: number) => string;
  /**
   * Called when a page/next/previous link is activated. Receives the target
   * 1-indexed page and the click event so a consumer can intercept navigation
   * (e.g. to drive client-side routing) via `event.preventDefault()`. Omit for
   * plain link navigation.
   */
  onNavigate?: (page: number, event: MouseEvent<HTMLAnchorElement>) => void;
  /**
   * When `false` (default), no ellipsis is shown: a fixed-size window of
   * consecutive pages slides to keep the current page centred, clamping at the
   * first/last page (`3 4 5 6 7`). When `true`, long ranges are condensed with
   * an ellipsis and the first/last pages stay pinned (`1 … 4 5 6 … 33`).
   */
  truncate?: boolean;
  /** Number of page links shown on each side of the current page. Defaults to 1. */
  siblingCount?: number;
  /** Number of page links pinned to the start/end boundaries. Defaults to 1. */
  boundaryCount?: number;
  className?: string;
}

type NormalizedItemType =
  | 'previous'
  | 'next'
  | 'page'
  | 'start-ellipsis'
  | 'end-ellipsis';

interface NormalizedItem {
  type: NormalizedItemType;
  page: number | null;
  selected?: boolean;
  disabled?: boolean;
}

/**
 * Builds a fixed-size sliding window of consecutive pages centred on the
 * current page (clamped at the boundaries), bracketed by previous/next
 * controls. No ellipsis or pinned boundary pages — used when `truncate={false}`.
 */
const buildWindowItems = (
  currentPage: number,
  totalPages: number,
  windowSize: number
): NormalizedItem[] => {
  // Guard against an out-of-range current page so the window, `aria-current`
  // and the previous/next targets stay valid.
  const current = Math.min(Math.max(currentPage, 1), totalPages);
  const size = Math.min(windowSize, totalPages);
  const half = Math.floor(size / 2);
  const end = Math.min(totalPages, Math.max(current + half, size));
  const start = Math.max(1, end - size + 1);

  const pages: NormalizedItem[] = [];
  for (let page = start; page <= end; page += 1) {
    pages.push({ type: 'page', page, selected: page === current });
  }

  return [
    { type: 'previous', page: current - 1, disabled: current <= 1 },
    ...pages,
    {
      type: 'next',
      page: current + 1,
      disabled: current >= totalPages,
    },
  ];
};

/**
 * Accessible, link-based pagination for paginated lists (PLPs, search results).
 *
 * Renders a `<nav aria-label="Pagination">` containing semantic `<a href>`
 * links for each page plus previous/next icon controls. The current page is a
 * non-link `<span aria-current="page">` and previous/next are disabled on the
 * first/last page. By default a fixed-size window slides to keep the current
 * page centred; with `truncate` long ranges are condensed with an ellipsis
 * (via MUI's `usePagination`) and the first/last pages stay pinned.
 */
const PaginationNav = ({
  currentPage,
  totalPages,
  createHref,
  onNavigate,
  truncate = false,
  siblingCount = 1,
  boundaryCount = 1,
  className,
}: PaginationNavProps): ReactElement | null => {
  const { t } = useTranslation('common');
  // Always call the hook (Rules of Hooks); its result is only used when
  // truncating. The truncated view pins the boundaries and inserts ellipses.
  const { items: truncatedItems } = usePagination({
    count: totalPages,
    page: currentPage,
    siblingCount,
    boundaryCount,
    showFirstButton: false,
    showLastButton: false,
  });

  // Keep the visible page count identical to the truncated view's widest form
  // (both boundaries + both sibling groups + current), so toggling `truncate`
  // slides the window rather than resizing it.
  const windowSize = 2 * boundaryCount + 2 * siblingCount + 1;
  const items: NormalizedItem[] = truncate
    ? truncatedItems.flatMap((item) => {
        // `showFirstButton`/`showLastButton` are off, so 'first'/'last' never
        // occur; skip them defensively to keep the normalized item type honest.
        if (item.type === 'first' || item.type === 'last') return [];
        return [
          {
            type: item.type,
            page: item.page,
            selected: item.selected,
            disabled: item.disabled,
          },
        ];
      })
    : buildWindowItems(currentPage, totalPages, windowSize);

  // A single page (or none) needs no navigation.
  if (totalPages <= 1) return null;

  return (
    <nav
      aria-label={t('pagination.label')}
      className={cn(styles.nav, className)}
    >
      <ul className={styles.list}>
        {items.map(({ type, page, selected, disabled }, index) => {
          const key = `${type}-${page ?? index}`;

          if (type === 'start-ellipsis' || type === 'end-ellipsis') {
            return (
              <li key={key} className={styles.item}>
                <span className={styles.ellipsis} aria-hidden="true">
                  &hellip;
                </span>
              </li>
            );
          }

          if (type === 'previous' || type === 'next') {
            const isNext = type === 'next';
            const CaretIcon = isNext ? CaretRightIcon : CaretLeftIcon;
            const label = isNext
              ? t('pagination.next')
              : t('pagination.previous');

            // Disabled on the first (previous) / last (next) page: rendered as a
            // non-interactive span so it is skipped by keyboard and assistive tech.
            if (disabled || page == null) {
              return (
                <li key={key} className={styles.item}>
                  <span
                    className={cn(styles.control, styles.controlDisabled)}
                    aria-hidden="true"
                  >
                    <CaretIcon size={16} aria-hidden="true" />
                  </span>
                </li>
              );
            }

            return (
              <li key={key} className={styles.item}>
                <a
                  href={createHref(page)}
                  className={styles.control}
                  aria-label={label}
                  onClick={(event) => onNavigate?.(page, event)}
                >
                  <CaretIcon size={16} aria-hidden="true" />
                </a>
              </li>
            );
          }

          // type === 'page'
          if (selected) {
            return (
              <li key={key} className={styles.item}>
                <span
                  className={cn(styles.page, styles.pageCurrent)}
                  aria-current="page"
                >
                  {page}
                </span>
              </li>
            );
          }

          return (
            <li key={key} className={styles.item}>
              <a
                href={createHref(page as number)}
                className={styles.page}
                onClick={(event) => onNavigate?.(page as number, event)}
              >
                {page}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default PaginationNav;
